/**
 * customHtml からスクリプト実行につながる要素・属性を除去して返す。
 * API レスポンス・キャッシュ両パスで、DOM 適用前に必ず通す。
 *
 * 除去対象:
 *   - 要素: script / iframe / frame / object / embed / form / base / meta / link / style / noscript / template
 *           SVG のアニメーション要素（animate / set など。href を javascript: に書き換えられるため）
 *   - 属性: on* イベントハンドラ / srcdoc
 *           値が javascript: / vbscript: で始まる属性（タブ・改行・制御文字を除いて判定する）
 *           URL 属性の data: のうち、画像・音声・動画・フォント以外
 *
 * style 属性は除去しない。ボタンの色・大きさなど見た目の変更は A/B テストの正当な施策であり、
 * customHtml は認証済みの管理者が登録するものなので、スクリプト実行につながるものだけを除去する方針とする。
 * noscript は、解析用の文書（スクリプト無効）とライブ DOM（スクリプト有効）で解析結果が変わる mXSS の原因になるため除去する。
 *
 * javascript: / vbscript: の判定は、URL 属性に限らずすべての属性にかける（意図した仕様）。
 * URL を値に取る属性は formaction・xlink:href・<animate> の values/to・サイト側スクリプトが使う data-* など多く、
 * 一覧で限定すると漏れがそのまま抜け道になるため。その代わり、title や alt が javascript: で始まる場合も属性ごと除去される。
 * 同じ理由で、on で始まる属性は将来追加されるイベント属性も含めてすべて除去する。
 */

const DANGEROUS_ELEMENTS = new Set([
  'script',
  'iframe',
  'frame',
  'frameset',
  'object',
  'embed',
  'form',
  'base',
  'meta',
  'link',
  'style',
  'noscript',
  // template の content（DocumentFragment）は querySelectorAll で走査されず、属性が除去されないまま残るため
  'template',
  // SVG のアニメーション要素
  'animate',
  'animatemotion',
  'animatetransform',
  'set',
  'discard',
]);

// URL を値に取りうる属性（data: スキームの判定対象）
const URL_ATTRIBUTES = new Set([
  'href',
  'xlink:href',
  'src',
  'srcset',
  'action',
  'formaction',
  'poster',
  'background',
  'data',
]);

const DANGEROUS_ATTRIBUTES = new Set(['srcdoc']);

// ブラウザは URL 解析時に ASCII のタブ・改行を無視し、前後の制御文字・空白を取り除く。
// 判定がすり抜けないよう、同じ文字を除いた値でスキームを確認する
const normalizeUrlValue = (value: string): string =>
  value.replace(/[\u0000- \u007f-\u009f]/g, '').toLowerCase();

const SCRIPT_SCHEME = /^(?:javascript|vbscript):/;
const DATA_SCHEME = /^data:/;
const SAFE_DATA_SCHEME = /^data:(?:image|audio|video|font)\//;

const XHTML_NAMESPACE = 'http://www.w3.org/1999/xhtml';

// 中身をテキストとして扱う要素。解析の文脈に使うと HTML が要素として解析されないため、div に置き換える
const RAW_TEXT_CONTEXTS = new Set([
  'script',
  'style',
  'textarea',
  'title',
  'xmp',
  'iframe',
  'noembed',
  'noframes',
  'noscript',
  'plaintext',
  'template',
]);

/**
 * 解析に使う文脈要素を、スクリプトが実行されない別文書の上に作る。
 * 挿入先（insertAdjacentHTML('afterend') なら置き換え対象の親要素）と同じ種類の要素にすることで、
 * <tr> / <li> / <option> など親要素に依存するタグや、SVG 内の要素がライブ DOM と同じ構造で解析される
 */
const createParsingContext = (doc: Document, context?: Element): Element => {
  if (!context) return doc.createElement('div');
  const namespace = context.namespaceURI ?? XHTML_NAMESPACE;
  const localName = context.localName.toLowerCase();
  if (namespace === XHTML_NAMESPACE) {
    return doc.createElement(
      RAW_TEXT_CONTEXTS.has(localName) ? 'div' : context.localName,
    );
  }
  return doc.createElementNS(namespace, context.localName);
};

/**
 * @param html - サニタイズする HTML
 * @param context - 挿入先の文脈となる要素（置き換え対象の親要素）。省略時は div の中として解析する
 */
export const sanitizeHtml = (html: string, context?: Element): string => {
  try {
    // スクリプトが実行されず、画像なども読み込まれない文書の上で解析する
    const doc = document.implementation.createHTMLDocument('');
    const container = createParsingContext(doc, context);
    container.innerHTML = html;

    // SVG の要素名は大文字小文字を区別するため、セレクタではなく localName で判定する
    container.querySelectorAll('*').forEach((el) => {
      if (DANGEROUS_ELEMENTS.has(el.localName.toLowerCase())) {
        el.remove();
      }
    });

    container.querySelectorAll('*').forEach((el) => {
      for (const attr of Array.from(el.attributes)) {
        const name = attr.name.toLowerCase();
        if (name.startsWith('on') || DANGEROUS_ATTRIBUTES.has(name)) {
          el.removeAttribute(attr.name);
          continue;
        }
        const value = normalizeUrlValue(attr.value);
        if (SCRIPT_SCHEME.test(value)) {
          el.removeAttribute(attr.name);
          continue;
        }
        if (
          URL_ATTRIBUTES.has(name) &&
          DATA_SCHEME.test(value) &&
          !SAFE_DATA_SCHEME.test(value)
        ) {
          el.removeAttribute(attr.name);
        }
      }
    });

    return container.innerHTML;
  } catch (e) {
    console.warn('[AgileBase] Failed to sanitize HTML:', e);
    return '';
  }
};
